import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  FiX,
  FiChevronDown,
  FiCheck,
  FiShield,
  FiUser,
  FiClock,
  FiAlertCircle,
  FiSearch,
  FiGrid,
  FiCheckCircle,
  FiPlus,
  FiTrash2,
  FiFileText,
  FiCalendar,
  FiMapPin,
  FiTag,
  FiRefreshCw,
} from "react-icons/fi";
import api from "../../../connector/api";

// Helper to reliably extract IDs from objects or primitive values
const getId = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object" && val.id !== undefined) return String(val.id);
  return String(val);
};

const INFRACTION_LEVELS = [
  { value: "MINOR", label: "Minor (Classroom Managed)" },
  { value: "MAJOR", label: "Major (Office Managed)" },
];

const VIOLATION_TYPES = [
  { value: "DISRUPTIVE_BEHAVIOR", label: "Disruptive Behavior" },
  { value: "TARDINESS", label: "Tardiness / Truancy" },
  { value: "DRESS_CODE", label: "Dress Code Violation" },
  { value: "ACADEMIC_DISHONESTY", label: "Academic Dishonesty" },
  { value: "INSUBORDINATION", label: "Insubordination / Defiance" },
  { value: "TECHNOLOGY_MISUSE", label: "Technology Misuse" },
  { value: "BULLYING_HARASSMENT", label: "Bullying / Harassment" },
  { value: "OTHER", label: "Other Violation" },
];

const INTERVENTION_TYPES = [
  { value: "VERBAL_WARNING", label: "Verbal Warning/Correction" },
  { value: "SEAT_REASSIGNMENT", label: "Seat Reassignment" },
  { value: "TEACHER_CONFERENCE", label: "Private Student-Teacher Conference" },
  { value: "PARENT_CONTACT", label: "Parent Contact/Email" },
  { value: "THINK_SHEET", label: 'Behavior "Think Sheet"' },
  { value: "OTHER", label: "Other" },
];

const BEHAVIOR_TYPES = [
  { value: "POSITIVE", label: "Positive Behavior / Recognition" },
  { value: "NEUTRAL", label: "General Observation / Check-in" },
  { value: "CONCERN", label: "Behavioral Concern" },
  { value: "OTHER", label: "Other Behavior Type" },
];

// --- TOAST NOTIFICATION COMPONENT ---
function ToastNotification({ toast, onClose }) {
  if (!toast) return null;

  const isSuccess = toast.type === "success";

  return ReactDOM.createPortal(
    <div
      className={`fixed top-5 right-5 z-[100000] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-lg text-white text-xs font-medium transition-all duration-200 animate-in slide-in-from-top-2 ${
        isSuccess ? "bg-emerald-600" : "bg-red-600"
      }`}
    >
      {isSuccess ? (
        <FiCheckCircle className="w-4 h-4 shrink-0 text-white" />
      ) : (
        <FiAlertCircle className="w-4 h-4 shrink-0 text-white" />
      )}
      <span className="truncate max-w-xs">{toast.message}</span>
      <button
        type="button"
        onClick={onClose}
        className="ml-2 text-white/80 hover:text-white transition-colors"
      >
        <FiX className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
}

// --- CUSTOM SINGLE PORTAL DROPDOWN ---
function CustomDropdown({ label, value, options, onChange, icon: Icon, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160),
      });
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium ${
          disabled ? "opacity-50 cursor-not-allowed bg-zinc-100" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[99999] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto custom-scroll"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  String(value) === String(option.value)
                    ? "bg-zinc-100 text-zinc-900 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {String(value) === String(option.value) && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// --- SEARCHABLE STUDENT PORTAL DROPDOWN ---
function SearchableStudentDropdown({ value, options, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 240),
      });
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-student-dropdown")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium ${
          disabled ? "opacity-50 cursor-not-allowed bg-zinc-100" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-2 truncate">
          <FiUser className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <span className="truncate text-zinc-700">
            {selectedOption ? selectedOption.label : "-- Select Student --"}
          </span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-student-dropdown fixed bg-white border border-zinc-200 rounded-lg shadow-xl p-2 z-[99999] animate-in fade-in zoom-in-95 duration-100 max-h-64 flex flex-col custom-scroll"
          >
            <div className="relative mb-2">
              <FiSearch className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search student..."
                className="w-full pl-8 pr-3 py-1 bg-zinc-50 border border-zinc-200 rounded-md text-xs focus:outline-none focus:border-zinc-400"
                autoFocus
              />
            </div>

            <div className="overflow-y-auto max-h-48 space-y-0.5">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-zinc-400 text-center">
                  No students found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left transition-colors text-xs rounded-md cursor-pointer ${
                      String(value) === String(option.value)
                        ? "bg-zinc-100 text-zinc-900 font-semibold"
                        : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                    {String(value) === String(option.value) && (
                      <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// --- MAIN BEHAVIOR LOG MODAL COMPONENT ---
export default function BehaviorLogModal({
  isOpen,
  onClose,
  selectedStudent,
  selectedRecord,
  subjectAssignmentId,
  classId,
  classroom,
  onSuccess,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  /* Classroom state */
  const [classrooms, setClassrooms] = useState([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState("");

  /* Student state */
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");

  /* Behavior fields */
  const [logDate, setLogDate] = useState(new Date().toISOString().split("T")[0]);
  const [logTime, setLogTime] = useState(new Date().toTimeString().slice(0, 5));
  const [location, setLocation] = useState("Classroom");

  const [behaviorType, setBehaviorType] = useState("POSITIVE");
  const [customBehaviorType, setCustomBehaviorType] = useState("");

  const [infractionLevel, setInfractionLevel] = useState("MINOR");
  const [violationType, setViolationType] = useState("DISRUPTIVE_BEHAVIOR");
  const [customViolationType, setCustomViolationType] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");

  const [notes, setNotes] = useState("");

  const [interventions, setInterventions] = useState([
    {
      intervention_type: "VERBAL_WARNING",
      date_attempted: new Date().toISOString().split("T")[0],
      other_details: "",
    },
  ]);

  const isEditMode = Boolean(selectedRecord);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Fetch Classrooms on Open
  useEffect(() => {
    if (!isOpen) return;
    let isSubscribed = true;

    const fetchClassrooms = async () => {
      setLoadingClassrooms(true);
      try {
        const response = await api.get("/academic/classroom/?page_size=100");
        if (!isSubscribed) return;
        const results = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setClassrooms(results);
      } catch (err) {
        if (isSubscribed) console.error("Failed to fetch classrooms:", err);
      } finally {
        if (isSubscribed) setLoadingClassrooms(false);
      }
    };

    fetchClassrooms();
    return () => {
      isSubscribed = false;
    };
  }, [isOpen]);

  // Initial classroom assignment
  useEffect(() => {
    if (!isOpen) return;
    const initialClassId = getId(
      classId || classroom?.id || classroom || selectedRecord?.classroom
    );
    if (initialClassId) {
      setSelectedClassroomId(initialClassId);
    }
  }, [isOpen, classId, classroom, selectedRecord]);

  // Fetch Students for selected classroom
  useEffect(() => {
    if (!isOpen || !selectedClassroomId) {
      setStudents([]);
      return;
    }

    let isSubscribed = true;

    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const response = await api.get(
          `/students/?classroom=${selectedClassroomId}&page_size=200`
        );
        if (!isSubscribed) return;
        const results = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setStudents(results);
      } catch (err) {
        if (isSubscribed) console.error("Failed to fetch students:", err);
      } finally {
        if (isSubscribed) setLoadingStudents(false);
      }
    };

    fetchStudents();
    return () => {
      isSubscribed = false;
    };
  }, [isOpen, selectedClassroomId]);

  // Reset / Populate Form Fields
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSelectedClassroomId("");
      setStudentId("");
      setNotes("");
      setIncidentDescription("");
      setLogDate(new Date().toISOString().split("T")[0]);
      setLogTime(new Date().toTimeString().slice(0, 5));
      setLocation("Classroom");
      setBehaviorType("POSITIVE");
      setCustomBehaviorType("");
      setInfractionLevel("MINOR");
      setViolationType("DISRUPTIVE_BEHAVIOR");
      setCustomViolationType("");
      setInterventions([
        {
          intervention_type: "VERBAL_WARNING",
          date_attempted: new Date().toISOString().split("T")[0],
          other_details: "",
        },
      ]);
      return;
    }

    if (selectedRecord) {
      const recordStudentId = getId(selectedRecord.student || selectedStudent);
      setStudentId(recordStudentId);
      setLogDate(
        selectedRecord.log_date ||
          selectedRecord.date_of_incident ||
          new Date().toISOString().split("T")[0]
      );
      setLogTime(
        selectedRecord.log_time ||
          selectedRecord.time_of_incident ||
          new Date().toTimeString().slice(0, 5)
      );
      setLocation(selectedRecord.location || "Classroom");
      setBehaviorType(selectedRecord.behavior_type || "POSITIVE");
      setCustomBehaviorType(
        selectedRecord.custom_behavior_type ||
          selectedRecord.other_behavior_details ||
          ""
      );
      setInfractionLevel(selectedRecord.infraction_level || "MINOR");
      setViolationType(selectedRecord.violation_type || "DISRUPTIVE_BEHAVIOR");
      setCustomViolationType(
        selectedRecord.custom_violation_type ||
          selectedRecord.other_violation_details ||
          ""
      );
      setIncidentDescription(selectedRecord.incident_description || "");
      setNotes(selectedRecord.notes || selectedRecord.description || "");

      if (
        Array.isArray(selectedRecord.interventions) &&
        selectedRecord.interventions.length > 0
      ) {
        setInterventions(
          selectedRecord.interventions.map((item) => ({
            intervention_type: item.intervention_type || "VERBAL_WARNING",
            date_attempted:
              item.date_attempted ||
              item.intervention_date ||
              new Date().toISOString().split("T")[0],
            other_details: item.other_details || "",
          }))
        );
      }
    } else if (selectedStudent) {
      const id = getId(selectedStudent);
      setStudentId(id);
    }
  }, [isOpen, selectedStudent, selectedRecord]);

  if (!isOpen) return null;

  /* Intervention handlers */
  const handleAddIntervention = () => {
    setInterventions([
      ...interventions,
      {
        intervention_type: "VERBAL_WARNING",
        date_attempted: new Date().toISOString().split("T")[0],
        other_details: "",
      },
    ]);
  };

  const handleRemoveIntervention = (index) => {
    setInterventions(interventions.filter((_, idx) => idx !== index));
  };

  const handleInterventionChange = (index, field, value) => {
    const updated = [...interventions];
    updated[index][field] = value;
    setInterventions(updated);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedClassroomId) {
      setError("Please select a classroom.");
      showToast("error", "Please select a classroom.");
      return;
    }

    if (!studentId) {
      setError("Please select a student.");
      showToast("error", "Please select a student.");
      return;
    }

    if (behaviorType === "OTHER" && !customBehaviorType.trim()) {
      setError("Please specify details for the custom behavior type.");
      showToast("error", "Please specify details for custom behavior.");
      return;
    }

    if (violationType === "OTHER" && !customViolationType.trim()) {
      setError("Please specify details for the custom violation.");
      showToast("error", "Please specify details for custom violation.");
      return;
    }

    setSubmitting(true);
    setError("");

    const payload = {
      student: parseInt(studentId, 10),
      classroom: parseInt(selectedClassroomId, 10),
      subject_assignment: subjectAssignmentId ? parseInt(subjectAssignmentId, 10) : null,
      log_date: logDate,
      log_time: logTime,
      date_of_incident: logDate,
      time_of_incident: logTime,
      location: location,
      behavior_type: behaviorType,
      custom_behavior_type: behaviorType === "OTHER" ? customBehaviorType : "",
      infraction_level: infractionLevel,
      violation_type: violationType,
      custom_violation_type: violationType === "OTHER" ? customViolationType : "",
      incident_description: incidentDescription,
      notes: notes,
      interventions: interventions.map((item) => ({
        intervention_type: item.intervention_type,
        date_attempted: item.date_attempted || null,
        other_details: item.intervention_type === "OTHER" ? item.other_details : "",
      })),
    };

    try {
      if (isEditMode) {
        await api.patch(`/discipline-referrals/${selectedRecord.id}/`, payload);
      } else {
        await api.post("/discipline-referrals/", payload);
      }

      showToast("success", "Behavior log saved successfully!");
      if (onSuccess) onSuccess();
      setTimeout(() => {
        onClose();
      }, 300);
    } catch (err) {
      console.error("Failed to save behavior log:", err);
      const backendErrors = err.response?.data;
      if (typeof backendErrors === "object") {
        const firstErrorKey = Object.keys(backendErrors)[0];
        const firstErrorMsg = Array.isArray(backendErrors[firstErrorKey])
          ? backendErrors[firstErrorKey][0]
          : backendErrors[firstErrorKey];
        setError(`${firstErrorKey}: ${firstErrorMsg}`);
        showToast("error", `${firstErrorKey}: ${firstErrorMsg}`);
      } else {
        const fallbackMsg = err.response?.data?.detail || "Failed to save behavior log record.";
        setError(fallbackMsg);
        showToast("error", fallbackMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const classroomOptions = [
    { value: "", label: "-- Select Classroom --" },
    ...classrooms.map((cls) => ({
      value: String(cls.id),
      label: cls.display_name || cls.name || (cls.section ? cls.section.name : `Classroom #${cls.id}`),
    })),
  ];

  const studentOptions = students.map((s) => {
    const nameParts = [
      s.last_name || s.user?.last_name,
      s.first_name || s.user?.first_name,
    ].filter(Boolean);

    const displayName =
      nameParts.length > 0
        ? nameParts.join(", ")
        : s.full_name || s.name || s.user?.full_name || `Student #${s.id}`;

    const studentIdStr = s.student_id_number || s.student_id || s.id_number || "No ID";

    return {
      value: String(s.id),
      label: `${displayName} (${studentIdStr})`,
    };
  });

  return (
    <>
      <ToastNotification toast={toast} onClose={() => setToast(null)} />
      {ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200 shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 bg-zinc-50/50 rounded-t-lg shrink-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-zinc-900 text-sm">
                  {isEditMode ? "Edit Behavior Log" : "Log Behavior Entry"}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-zinc-400 hover:text-zinc-700 rounded-md transition-colors cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="p-5 space-y-4 overflow-y-auto custom-scroll flex-1 text-xs"
            >
              {error && (
                <div className="p-2.5 rounded-lg bg-red-50 border border-red-200/60 text-red-600 text-xs flex items-center gap-2">
                  <FiAlertCircle className="w-4 h-4 shrink-0" />
                  <span className="truncate">{error}</span>
                </div>
              )}

              {/* Classroom & Student Row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Classroom <span className="text-red-500">*</span>
                  </label>
                  <CustomDropdown
                    label="Select Classroom"
                    value={selectedClassroomId}
                    options={classroomOptions}
                    onChange={(val) => {
                      setSelectedClassroomId(val);
                      setStudentId("");
                    }}
                    icon={FiGrid}
                    disabled={loadingClassrooms || isEditMode}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Student <span className="text-red-500">*</span>
                  </label>
                  <SearchableStudentDropdown
                    value={studentId}
                    options={studentOptions}
                    onChange={(val) => setStudentId(val)}
                    disabled={!selectedClassroomId || loadingStudents || isEditMode}
                  />
                </div>
              </div>

              {/* Log Date, Time & Location Row */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={logDate}
                    onChange={(e) => setLogDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    required
                    value={logTime}
                    onChange={(e) => setLogTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Room 101"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>
              </div>

              {/* Behavior Category & Violation Type Row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-zinc-700">
                    Behavior Category
                  </label>
                  <CustomDropdown
                    value={behaviorType}
                    options={BEHAVIOR_TYPES}
                    onChange={(val) => {
                      setBehaviorType(val);
                      if (val !== "OTHER") setCustomBehaviorType("");
                    }}
                    icon={FiTag}
                  />
                  {behaviorType === "OTHER" && (
                    <input
                      type="text"
                      required
                      placeholder="Specify custom behavior details..."
                      value={customBehaviorType}
                      onChange={(e) => setCustomBehaviorType(e.target.value)}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-zinc-700">
                    Violation Type
                  </label>
                  <CustomDropdown
                    value={violationType}
                    options={VIOLATION_TYPES}
                    onChange={(val) => {
                      setViolationType(val);
                      if (val !== "OTHER") setCustomViolationType("");
                    }}
                    icon={FiShield}
                  />
                  {violationType === "OTHER" && (
                    <input
                      type="text"
                      required
                      placeholder="Specify custom violation details..."
                      value={customViolationType}
                      onChange={(e) => setCustomViolationType(e.target.value)}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                  )}
                </div>
              </div>

              {/* Infraction Level */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  Infraction Level
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {INFRACTION_LEVELS.map((level) => (
                    <label
                      key={level.value}
                      className={`px-3 py-2 border rounded-lg flex items-center gap-2 cursor-pointer transition-colors text-xs select-none ${
                        infractionLevel === level.value
                          ? "bg-zinc-900 text-white border-zinc-900 font-medium"
                          : "bg-zinc-50/80 text-zinc-700 border-zinc-200/80 hover:bg-zinc-100/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name="infraction_level"
                        value={level.value}
                        checked={infractionLevel === level.value}
                        onChange={(e) => setInfractionLevel(e.target.value)}
                        className="hidden"
                      />
                      <div
                        className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                          infractionLevel === level.value
                            ? "border-white bg-white"
                            : "border-zinc-300 bg-white"
                        }`}
                      >
                        {infractionLevel === level.value && (
                          <div className="w-1.5 h-1.5 rounded-full bg-zinc-900" />
                        )}
                      </div>
                      <span className="truncate">{level.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Classroom Interventions Attempted */}
              <div className="space-y-2 pt-2 border-t border-zinc-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-700">
                    Interventions Attempted
                  </span>
                  <button
                    type="button"
                    onClick={handleAddIntervention}
                    className="text-zinc-900 hover:text-zinc-600 font-medium flex items-center gap-1 text-[11px] cursor-pointer"
                  >
                    <FiPlus className="w-3.5 h-3.5" /> Add Intervention
                  </button>
                </div>

                {interventions.map((item, index) => (
                  <div
                    key={index}
                    className="p-2.5 border border-zinc-200/80 rounded-lg bg-zinc-50/50 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <CustomDropdown
                          value={item.intervention_type}
                          options={INTERVENTION_TYPES}
                          onChange={(val) =>
                            handleInterventionChange(index, "intervention_type", val)
                          }
                        />
                      </div>

                      <input
                        type="date"
                        value={item.date_attempted}
                        onChange={(e) =>
                          handleInterventionChange(
                            index,
                            "date_attempted",
                            e.target.value
                          )
                        }
                        className="px-2 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                      />

                      {interventions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveIntervention(index)}
                          className="p-1.5 text-zinc-400 hover:text-red-600 rounded transition-colors cursor-pointer"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {item.intervention_type === "OTHER" && (
                      <input
                        type="text"
                        placeholder="Specify details for 'Other'..."
                        value={item.other_details}
                        onChange={(e) =>
                          handleInterventionChange(
                            index,
                            "other_details",
                            e.target.value
                          )
                        }
                        className="w-full px-2.5 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Incident Description */}
              <div className="pt-2 border-t border-zinc-100">
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  Incident Description
                </label>
                <textarea
                  rows="2"
                  value={incidentDescription}
                  onChange={(e) => setIncidentDescription(e.target.value)}
                  placeholder="Provide a factual narrative of the incident..."
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400 resize-none"
                />
              </div>

              {/* Observations & Notes */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">
                  Observations & Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows="2"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Provide observations or action items..."
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3.5 py-1.5 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg text-xs font-medium transition-colors shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {submitting && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {submitting
                    ? "Saving..."
                    : isEditMode
                    ? "Update Record"
                    : "Log Behavior"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}